import { Box, StandardForm, Typography, FormField, FormInputGroup, InputWithFloatingLabel, FloatingLabel, TextInput, FormError, AlertBox, FlexBox, Checkbox, Link, Button } from '@vp/swan';
import React, { useContext, useState, useEffect } from 'react';
import { CAContext } from './src-contexts-CAContext.js';
import { validateEmail, validateName } from './src-utils-validation-utils.js';
import { getPrivacyPolicyLink } from './src-config-privacy-link.js';

const PrechatFormMIAW = ({ onSubmit }) => {
  const {
    chatState: { userInfo, availableSchedule, gcpChatData, chatSession, locale }
  } = useContext(CAContext);
  const [showofflineAlert, setShowOfflineAlert] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showNearOfflineAlert, setShowNearOfflineAlert] = useState(false);
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "" });
  const [errors, setErrors] = useState({ firstName: "", lastName: "", email: "" });
  const [touched, setTouched] = useState({ firstName: false, lastName: false, email: false });
  const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState(false);
  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    if (field === "email") {
      setErrors({ ...errors, [field]: validateEmail(formData[field]) });
    } else {
      setErrors({ ...errors, [field]: validateName(formData[field], field === "firstName" ? "First name" : "Last name") });
    }
  };
  const validateForm = () => {
    const newErrors = {
      firstName: validateName(formData.firstName, "First name"),
      lastName: validateName(formData.lastName, "Last name"),
      email: validateEmail(formData.email)
    };
    setErrors(newErrors);
    setTouched({ firstName: true, lastName: true, email: true });
    return !Object.values(newErrors).some((error) => error !== "");
  };
  const handleSubmit = () => {
    if (userInfo?.isLoggedIn) {
      onSubmit?.({
        email: userInfo.userData?.email || "",
        firstName: userInfo.userData?.firstName || "",
        lastName: userInfo.userData?.lastName || ""
      });
      setShowLoading(true);
      return;
    } else if (validateForm()) {
      onSubmit?.({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      setShowLoading(true);
    }
  };
  useEffect(() => {
    if (userInfo?.isLoggedIn) {
      setFormData({
        firstName: userInfo.userData?.firstName || "",
        lastName: userInfo.userData?.lastName || "",
        email: userInfo.userData?.email || ""
      });
    }
  }, [userInfo]);
  useEffect(() => {
    if (gcpChatData) {
      const { isChatEnabled, remainingTime } = gcpChatData.status;
      if (isChatEnabled && !isNaN(remainingTime) && remainingTime > 0) {
        setTimeout(() => {
          setShowOfflineAlert(true);
          setShowNearOfflineAlert(false);
        }, remainingTime * 1e3);
        if (remainingTime > 600) {
          setTimeout(
            () => {
              setShowNearOfflineAlert(true);
            },
            (remainingTime - 600) * 1e3
          );
        } else {
          setShowNearOfflineAlert(true);
        }
      }
    }
  }, [gcpChatData]);
  useEffect(() => {
    if (chatSession === "miawChatSession") {
      setShowLoading(false);
      setIsPrivacyPolicyAccepted(false);
      setFormData({ firstName: "", lastName: "", email: "" });
      setErrors({ firstName: "", lastName: "", email: "" });
      setTouched({ firstName: false, lastName: false, email: false });
    }
  }, [chatSession]);
  return /* @__PURE__ */ React.createElement(Box, { compactMode: true, marginY: 9, marginX: 5 }, /* @__PURE__ */ React.createElement(StandardForm, { marginX: 4 }, userInfo?.isLoggedIn ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Typography, { fontSkin: "title-section", mb: "3" }, "Privacy and Cookie Policy")) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Typography, { fontSkin: "title-section", mb: "3" }, "Hi there!"), /* @__PURE__ */ React.createElement(Typography, { fontSkin: "body-standard", mb: "between-subsections" }, "Let's start by adding your details.", /* @__PURE__ */ React.createElement("br", null), "All fields are required."), /* @__PURE__ */ React.createElement(FormField, { mb: "5" }, /* @__PURE__ */ React.createElement(FormInputGroup, null, /* @__PURE__ */ React.createElement(InputWithFloatingLabel, null, /* @__PURE__ */ React.createElement(FloatingLabel, null, "First name"), /* @__PURE__ */ React.createElement(
    TextInput,
    {
      value: formData.firstName,
      onChange: (e) => setFormData({ ...formData, firstName: e.target.value }),
      onBlur: () => handleBlur("firstName"),
      placeholder: "First name",
      disabled: showofflineAlert
    }
  )), " ", touched.firstName && errors.firstName && /* @__PURE__ */ React.createElement(FormError, null, errors.firstName))), /* @__PURE__ */ React.createElement(FormField, { mb: "5" }, /* @__PURE__ */ React.createElement(FormInputGroup, null, /* @__PURE__ */ React.createElement(InputWithFloatingLabel, null, /* @__PURE__ */ React.createElement(FloatingLabel, null, "Last name"), /* @__PURE__ */ React.createElement(
    TextInput,
    {
      value: formData.lastName,
      onChange: (e) => setFormData({ ...formData, lastName: e.target.value }),
      onBlur: () => handleBlur("lastName"),
      placeholder: "Last name",
      disabled: showofflineAlert
    }
  )), " ", touched.lastName && errors.lastName && /* @__PURE__ */ React.createElement(FormError, null, errors.lastName), " ")), /* @__PURE__ */ React.createElement(FormField, { mb: "between-actions" }, /* @__PURE__ */ React.createElement(FormInputGroup, null, /* @__PURE__ */ React.createElement(InputWithFloatingLabel, null, /* @__PURE__ */ React.createElement(FloatingLabel, null, "Email linked to your VistaPrint account"), /* @__PURE__ */ React.createElement(
    TextInput,
    {
      value: formData.email,
      onChange: (e) => setFormData({ ...formData, email: e.target.value }),
      onBlur: () => handleBlur("email"),
      placeholder: "Email linked to your VistaPrint account",
      disabled: showofflineAlert
    }
  )), " ", touched.email && errors.email && /* @__PURE__ */ React.createElement(FormError, null, errors.email)))), showofflineAlert && /* @__PURE__ */ React.createElement(Box, { mb: "to-actions" }, /* @__PURE__ */ React.createElement(AlertBox, { skin: "warning" }, "Our specialists are currently offline.", /* @__PURE__ */ React.createElement("br", null), "Chat will be available again at ", availableSchedule?.weekday, ",", availableSchedule?.startTime)), showNearOfflineAlert && /* @__PURE__ */ React.createElement(Box, { mb: "to-actions" }, /* @__PURE__ */ React.createElement(AlertBox, { skin: "warning" }, "Our specialists will be signing off soon. The chat will be available again at ", availableSchedule?.weekday, ",", availableSchedule?.startTime)), /* @__PURE__ */ React.createElement(FlexBox, { gap: "between-icon-and-text", mb: "to-actions" }, /* @__PURE__ */ React.createElement(
    Checkbox,
    {
      style: { minWidth: "20px" },
      checked: isPrivacyPolicyAccepted,
      onChange: () => setIsPrivacyPolicyAccepted(!isPrivacyPolicyAccepted),
      name: "checkbox",
      disabled: showofflineAlert
    }
  ), /* @__PURE__ */ React.createElement(Typography, { fontSkin: "body-small", textColor: "subtle" }, "I agree to VistaPrint and its chat service provider recording and using chat communications as described in the", " ", /* @__PURE__ */ React.createElement(Link, { href: getPrivacyPolicyLink(locale) }, "Privacy and Cookie Policy."))), /* @__PURE__ */ React.createElement(FlexBox, null, /* @__PURE__ */ React.createElement(Button, { onClick: handleSubmit, skin: "primary", disabled: !isPrivacyPolicyAccepted || showofflineAlert, loading: showLoading }, "Chat now"))));
};

export { PrechatFormMIAW as default };
//# sourceMappingURL=src-components-PrechatFormMIAW-PrechatFormMIAW.js.map
